import React, { Fragment, useState, useEffect } from "react";
import axios from 'axios';
import { FaPlus, FaGift, FaEdit, FaTrash, FaSpinner } from 'react-icons/fa';
import AdminPageLoader from './AdminPageLoader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0
  }).format(amount);
}

function ComplimentaryRulesPage() {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [formData, setFormData] = useState({ name: '', cartValue: '', complimentaryValue: '' });

    useEffect(() => { fetchRules(); }, []);

    const fetchRules = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/complimentary-rules`);
            setRules(response.data);
        } catch (error) {
            console.error("Error fetching rules:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingRule) {
                await axios.put(`${API_BASE_URL}/complimentary-rule/${editingRule._id}`, formData);
            } else {
                await axios.post(`${API_BASE_URL}/complimentary-rule`, formData);
            }
            fetchRules();
            setShowModal(false);
            setEditingRule(null);
            setFormData({ name: '', cartValue: '', complimentaryValue: '' });
        } catch (error) {
            console.error("Error saving rule:", error);
        }
    };

    const handleEdit = (rule) => {
        setEditingRule(rule);
        setFormData({ name: rule.name, cartValue: rule.cartValue, complimentaryValue: rule.complimentaryValue });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this rule?')) {
            try {
                await axios.delete(`${API_BASE_URL}/complimentary-rule/${id}`);
                fetchRules();
            } catch (error) {
                console.error("Error deleting rule:", error);
            }
        }
    };

    // Lock body scroll when modal open
    useEffect(() => {
        if (showModal) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = originalOverflow; };
        }
    }, [showModal]);

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                {/* Heading */}
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">
                                    COMPLIMENTARY RULES ({rules.length} TOTAL)
                                </h4>
                                <p className="text-muted p-0 m-0">Set cart value limits and complimentary product values</p>
                            </div>
                            <button
                                type="button"
                                className="btn theme-btn-blue-flat btn-sm rounded-0"
                                style={{
                                    padding: '6px 16px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                                onClick={() => {
                                    setEditingRule(null);
                                    setFormData({ name: '', cartValue: '', complimentaryValue: '' });
                                    setShowModal(true);
                                }}
                            >
                                <FaPlus size={11} /> Add Rule
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                {loading ? (
                    <AdminPageLoader message="Loading complimentary rules..." />
                ) : (
                    <div className="row orders-table-row mt-4">
                        <div className="col-12">
                            <div className="table-responsive" style={{ border: '1px solid #d1d5db' }}>
                                <table className="table table-hover orders-table mb-0">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '8%', minWidth: '50px' }}>#</th>
                                            <th style={{ width: '37%', minWidth: '220px' }}>Rule Name</th>
                                            <th style={{ width: '25%', minWidth: '180px' }}>Cart Value Limit</th>
                                            <th style={{ width: '22%', minWidth: '180px' }}>Free Products Value Limit</th>
                                            <th style={{ width: '8%', minWidth: '80px', textAlign: 'center' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rules.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" className="text-center py-5 text-muted bg-white" style={{ fontSize: '14px' }}>
                                                    No rules added yet. Click the "Add Rule" button to get started.
                                                </td>
                                            </tr>
                                        ) : (
                                            rules.map((rule, idx) => (
                                                <tr key={rule._id}>
                                                    <td>
                                                        <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                                                            {idx + 1}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <strong style={{ color: '#1a237e', fontSize: '14px' }}>
                                                            {rule.name}
                                                        </strong>
                                                    </td>
                                                    <td>
                                                        <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                                                            {formatCurrency(rule.cartValue)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-warning text-dark" style={{ fontWeight: '600', fontSize: '12px', padding: '4px 8px', borderRadius: '4px' }}>
                                                            {formatCurrency(rule.complimentaryValue)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <button
                                                                type="button"
                                                                className="btn-action-square btn-action-view"
                                                                title="Edit Rule"
                                                                onClick={() => handleEdit(rule)}
                                                            >
                                                                <FaEdit size={12} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn-action-square btn-action-cancel"
                                                                title="Delete Rule"
                                                                onClick={() => handleDelete(rule._id)}
                                                            >
                                                                <FaTrash size={12} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="assign-modal-overlay" onClick={() => setShowModal(false)}>
                    <div 
                        className="assign-modal-content" 
                        style={{ width: '450px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} 
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="assign-modal-header">
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: '32px', height: '32px', borderRadius: '8px',
                                    background: '#e8eaf6', color: '#1a237e',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontSize: '15px'
                                }}>
                                    <FaGift />
                                </div>
                                <div className="d-flex flex-column align-items-start">
                                    <h5 style={{ color: '#0f172a', fontSize: '16px', fontWeight: '700', margin: 0 }}>
                                        {editingRule ? 'Edit Rule' : 'Add New Rule'}
                                    </h5>
                                    <span style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>
                                        Configure cart value threshold and reward rules
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                style={{
                                    border: 'none', background: 'none', fontSize: '20px',
                                    color: '#94a3b8', cursor: 'pointer', padding: '0 2px',
                                    lineHeight: 1, transition: 'color 0.2s'
                                }}
                                onMouseOver={e => e.currentTarget.style.color = '#0f172a'}
                                onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Body */}
                        <form onSubmit={handleSubmit} style={{ margin: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <div className="assign-modal-body" style={{ overflowY: 'auto', flex: '1 1 auto', padding: '20px' }}>
                                <div className="mb-3">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>Rule Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={{ border: '1px solid #cbd5e1', borderRadius: '4px', height: '38px', fontSize: '13px', padding: '8px 12px' }}
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        required
                                        placeholder="e.g., Standard Rule"
                                    />
                                    <small style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>Name to identify this rule</small>
                                </div>
                                <div className="mb-3">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>Cart Value Limit (₹)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        style={{ border: '1px solid #cbd5e1', borderRadius: '4px', height: '38px', fontSize: '13px', padding: '8px 12px' }}
                                        value={formData.cartValue}
                                        onChange={(e) => setFormData({...formData, cartValue: Number(e.target.value)})}
                                        min="0"
                                        required
                                        placeholder="e.g., 500"
                                    />
                                    <small style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>Minimum cart value required</small>
                                </div>
                                <div className="mb-3">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>Free Products Value Limit (₹)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        style={{ border: '1px solid #cbd5e1', borderRadius: '4px', height: '38px', fontSize: '13px', padding: '8px 12px' }}
                                        value={formData.complimentaryValue}
                                        onChange={(e) => setFormData({...formData, complimentaryValue: Number(e.target.value)})}
                                        min="0"
                                        required
                                        placeholder="e.g., 100"
                                    />
                                    <small style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>Maximum value of complimentary products user can select</small>
                                </div>
                            </div>
                            <div className="assign-modal-footer">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    style={{
                                        flex: 1, padding: '8px 16px', borderRadius: '6px',
                                        border: '1px solid #e2e8f0', background: '#ffffff',
                                        color: '#374151', fontSize: '13px', fontWeight: '500',
                                        cursor: 'pointer', transition: 'all 0.2s'
                                    }}
                                    onMouseOver={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                                    onMouseOut={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    style={{
                                        flex: 1, padding: '8px 16px', borderRadius: '6px',
                                        border: 'none', background: '#1a237e',
                                        color: '#ffff01', fontSize: '13px', fontWeight: '600',
                                        cursor: 'pointer', transition: 'all 0.2s',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                    }}
                                >
                                    {editingRule ? 'Update Rule' : 'Add Rule'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Fragment>
    );
}

export default ComplimentaryRulesPage;
